import classNames from 'classnames';
import React from 'react';

import { FormattedMessage } from '@futrli/localisation';
import { DATASOURCES_STATUS, PREDICT_PRODUCT_SLUG } from '@futrli/shared-services';
import { DataSource } from '@futrli-sdk/organisations';

import styles from './sync-status.scss';

enum MessagePosition {
  top = 'top',
  bottom = 'bottom'
}

enum Size {
  small = 'small',
  medium = 'medium'
}

interface Props {
  messagePosition?: keyof typeof MessagePosition;
  size?: keyof typeof Size;
  statusContainerClassName?: string;
  datasource: DataSource;
}

export const SyncStatusProgressBar = ({
  messagePosition = MessagePosition.top,
  size = Size.small,
  statusContainerClassName,
  datasource
}: Props) => {
  const isMsgPlaceBelowProgressBar = messagePosition === MessagePosition.bottom;

  const isSyncStarting = [
    DATASOURCES_STATUS.connect_success,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_initial_start
  ].includes(datasource.status.value as DATASOURCES_STATUS);

  const isSyncInProgress = [
    DATASOURCES_STATUS.sync_update_status,
    DATASOURCES_STATUS.sync_initial_update_status
  ].includes(datasource.status.value as DATASOURCES_STATUS);

  if (isSyncStarting || isSyncInProgress) {
    return (
      <div className={styles.syncStatus}>
        <div className={classNames(styles.statusContainer, statusContainerClassName)}>
          <p className={classNames({ [styles.bottomMessage]: isMsgPlaceBelowProgressBar })}>
            <FormattedMessage
              id="datasource.data_is_syncing"
              defaultMessage="Your data is flowing in beautifully&hellip;"
            />
          </p>
          <div
            className={classNames(styles.progressBar, styles[PREDICT_PRODUCT_SLUG], styles.inProgress, {
              [styles.medium]: size === Size.medium
            })}
          />
        </div>
      </div>
    );
  }

  const hasSyncSucceded = [DATASOURCES_STATUS.sync_success, DATASOURCES_STATUS.sync_initial_success].includes(
    datasource.status.value as DATASOURCES_STATUS
  );

  if (hasSyncSucceded) {
    return (
      <div className={classNames(styles.statusContainer, statusContainerClassName)}>
        <p className={classNames({ [styles.bottomMessage]: isMsgPlaceBelowProgressBar })}>
          <FormattedMessage id="datasource.data_ready" defaultMessage="Your data is ready to go" />
        </p>
        <div
          className={classNames(styles.progressBar, styles[PREDICT_PRODUCT_SLUG], styles.success, {
            [styles.medium]: size === Size.medium
          })}
        />
      </div>
    );
  }

  const hasSyncFailed = [DATASOURCES_STATUS.sync_failure, DATASOURCES_STATUS.sync_initial_failure].includes(
    datasource.status.value as DATASOURCES_STATUS
  );

  if (hasSyncFailed) {
    return (
      <div className={classNames(styles.statusContainer, statusContainerClassName)}>
        <p className={classNames({ [styles.bottomMessage]: isMsgPlaceBelowProgressBar })}>
          <FormattedMessage id="datasource.data_sync_failed" defaultMessage="Data sync failed" />
        </p>
        <div
          className={classNames(styles.progressBar, styles[PREDICT_PRODUCT_SLUG], styles.failure, {
            [styles.medium]: size === Size.medium
          })}
        />
      </div>
    );
  }

  return null;
};
